import React, { useState, useEffect, useRef } from 'react';
import { IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useParams } from 'react-router-dom';
import { OrderBookData, TradingDataService } from '../../../../services/trading-data.service';
import { useCexSocketConnected, useExoticMode, useIsExRoute } from '../../../../recoil';
import { CexEngineSocketService, getCexEngineSocket } from '../../../../services/cex-engine-socket.service';
import { getOrderBook } from '../../../../services/cex-engine.service';

interface OrderBookProps {
  symbol?: string;
  isNotMobile?: boolean;
}

// Connection status indicator
const getStatusColor = (connectionStatus: string) => {
  switch (connectionStatus) {
    case 'connected':
      return 'text-green-500';
    case 'connecting':
      return 'text-yellow-500';
    case 'error':
      return 'text-red-500';
    default:
      return 'text-gray-500';
  }
};

const getStatusText = (connectionStatus: string) => {
  switch (connectionStatus) {
    case 'connected':
      return '●';
    case 'connecting':
      return '○';
    case 'error':
      return '✕';
    default:
      return '○';
  }
};

// Calculate total for display with consistent length
const calculateTotal = (price: string, size: string) => {
  const total = parseFloat(price) * parseFloat(size);
  if (total >= 1000) {
    return total.toFixed(2);
  } else if (total >= 1) {
    return total.toFixed(4);
  } else {
    return total.toFixed(6);
  }
};


const OrderBook: React.FC<OrderBookProps> = ({
  symbol = 'BTC-USDT',
  isNotMobile = true,
}) => {
  const isExotic = useIsExRoute();

  const params = useParams<{ market: string; }>();
  const [collapsed, setCollapsed] = useState(false);
  const [orderBookData, setOrderBookData] = useState<OrderBookData | null>(
    null
  );
  const [connectionStatus, setConnectionStatus] = useState<
    'connecting' | 'connected' | 'disconnected' | 'error'
  >('disconnected');
  const [lastPrice, setLastPrice] = useState<string>('0');
  const [maxRows, setMaxRows] = useState(9);

  const wsRef = useRef<WebSocket | null>(null);
  const tradingServiceRef = useRef<TradingDataService | null>(null);
  const cexEngineServiceRef = useRef<CexEngineSocketService | null>(null);
  const currentSymbolRef = useRef<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const [cexConnected] = useCexSocketConnected();

  const toggleCollapse = () => setCollapsed(!collapsed);

  // Get the current market symbol from params or fallback to prop
  const currentSymbol = params.market || symbol;

  // Normalize symbol for KuCoin API (convert USD to USDT)

  useEffect(() => {
    if (!isExotic || !cexConnected) return;

    // Update the current symbol ref
    currentSymbolRef.current = currentSymbol;

    // Initialize trading service
    cexEngineServiceRef.current = getCexEngineSocket();

    // Fetch initial order book data from API
    const fetchInitialOrderBook = async () => {
      try {
        const initialData = await getOrderBook(
          currentSymbol
        );
        // Only set data if it's for the current symbol
        if (initialData?.symbol === currentSymbolRef.current) {
          setOrderBookData(initialData);
          setConnectionStatus('connected');
          // Calculate last price from best bid/ask
          if (initialData.bids.length > 0 && initialData.asks.length > 0) {
            const bestBid = parseFloat(initialData.bids[0].price);
            const bestAsk = parseFloat(initialData.asks[0].price);
            const midPrice = ((bestBid + bestAsk) / 2).toFixed(2);
            setLastPrice(midPrice);
          }
        }
      } catch (error) {
        console.error('Failed to fetch initial order book data:', error);
        // Still try to connect websocket even if API fails
      }
    };

    // Connect to order book WebSocket
    const connectOrderBook = () => {
      // Set connecting status
      setConnectionStatus('connecting');

      try {
        cexEngineServiceRef.current?.onOrderbookUpdate(
          (orderBook: OrderBookData) => {
            // Only process data if it's for the current symbol to prevent race conditions
            if (orderBook.symbol === currentSymbolRef.current) {
              setOrderBookData(orderBook);
              setConnectionStatus('connected');
              // Calculate last price from best bid/ask
              if (orderBook.bids.length > 0 && orderBook.asks.length > 0) {
                const bestBid = parseFloat(orderBook.bids[0].price);
                const bestAsk = parseFloat(orderBook.asks[0].price);
                const midPrice = ((bestBid + bestAsk) / 2).toFixed(2);
                setLastPrice(midPrice);
              }
            }
          });
        setTimeout(() => {
          cexEngineServiceRef.current?.subscribeOrderbook({
            symbol: currentSymbol,
            depth: 20
          });
        }, 0);
      } catch (error) {
        console.error('Failed to connect to order book WebSocket:', error);
        setConnectionStatus('error');
      }
    };

    // Fetch initial data first, then connect websocket
    fetchInitialOrderBook().then(() => {
      connectOrderBook();
    });

    // Cleanup on unmount or when symbol changes
    return () => {
      cexEngineServiceRef.current?.unsubscribeOrderbook(currentSymbol);
    };
  }, [isExotic, cexConnected, currentSymbol]);

  // Fetch order data from KuCoin API
  useEffect(() => {
    if (isExotic) return;

    // Update the current symbol ref
    currentSymbolRef.current = currentSymbol;

    // Initialize trading service
    tradingServiceRef.current = new TradingDataService();

    // Fetch initial order book data from API
    const fetchInitialOrderBook = async () => {
      try {
        const initialData = await tradingServiceRef.current!.getOrderBook(
          currentSymbol
        );
        // Only set data if it's for the current symbol
        if (initialData?.symbol === currentSymbolRef.current) {
          setOrderBookData(initialData);
          setConnectionStatus('connected');

          // Calculate last price from best bid/ask
          if (initialData.bids.length > 0 && initialData.asks.length > 0) {
            const bestBid = parseFloat(initialData.bids[0].price);
            const bestAsk = parseFloat(initialData.asks[0].price);
            const midPrice = ((bestBid + bestAsk) / 2).toFixed(2);
            setLastPrice(midPrice);
          }
        }
      } catch (error) {
        console.error('Failed to fetch initial order book data:', error);
        // Still try to connect websocket even if API fails
      }
    };

    // Connect to order book WebSocket
    const connectOrderBook = () => {
      // Set connecting status
      setConnectionStatus('connecting');

      try {
        wsRef.current = tradingServiceRef.current!.connectOrderBookWebSocket(
          currentSymbol,
          (orderBook: OrderBookData) => {
            // Only process data if it's for the current symbol to prevent race conditions
            if (orderBook.symbol === currentSymbolRef.current) {
              setOrderBookData(orderBook);
              setConnectionStatus('connected');

              // Calculate last price from best bid/ask
              if (orderBook.bids.length > 0 && orderBook.asks.length > 0) {
                const bestBid = parseFloat(orderBook.bids[0].price);
                const bestAsk = parseFloat(orderBook.asks[0].price);
                const midPrice = ((bestBid + bestAsk) / 2).toFixed(2);
                setLastPrice(midPrice);
              }
            }
          }
        );

        // Handle WebSocket events
        if (wsRef.current) {
          wsRef.current.addEventListener('error', (event) => {
            // Only handle error if it's for the current symbol
            if (currentSymbolRef.current === currentSymbol) {
              setConnectionStatus('error');
            }
          });

          wsRef.current.addEventListener('close', (event) => {
            // Only handle close if it's for the current symbol
            if (currentSymbolRef.current === currentSymbol) {
              setConnectionStatus('disconnected');
            }
          });
        }
      } catch (error) {
        console.error('Failed to connect to order book WebSocket:', error);
        setConnectionStatus('error');
      }
    };

    // Fetch initial data first, then connect websocket
    fetchInitialOrderBook().then(() => {
      connectOrderBook();
    });

    // Cleanup on unmount or when symbol changes
    return () => {
      if (wsRef.current) {
        // Remove event listeners
        wsRef.current.removeEventListener('error', () => { });
        wsRef.current.removeEventListener('close', () => { });
        // Close the WebSocket
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isExotic, currentSymbol]);

  // Calculate max rows based on container height
  useEffect(() => {
    if (!collapsed && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const height = rect.height;
      // Approximate heights: header ~32px, current price ~32px, padding/margins ~16px
      const availableHeight = height - 64 - 16;
      const rowHeight = 26; // approximate row height
      const rowsPerSide = Math.floor(availableHeight / rowHeight / 2);
      setMaxRows(Math.min(Math.max(rowsPerSide, 1), 25));
    }
  }, [collapsed, orderBookData]);

  // Format price for display with consistent length
  const formatPrice = (price: string) => {
    const num = parseFloat(price);
    if (num >= 1000) {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } else if (num >= 1) {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      });
    } else {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 6,
        maximumFractionDigits: 8,
      });
    }
  };

  // Format size for display with consistent length
  const formatSize = (size: string) => {
    const num = parseFloat(size);
    if (num >= 1000) {
      return num.toFixed(2);
    } else if (num >= 1) {
      return num.toFixed(4);
    } else {
      return num.toFixed(6);
    }
  };


  // Get display data (limit to maxRows entries each for asks/bids)
  const displayAsks = orderBookData?.asks.slice(0, maxRows).reverse() || [];
  const displayBids = orderBookData?.bids.slice(0, maxRows) || [];

  // Calculate cumulative sizes for depth visualization
  const originalAsks = orderBookData?.asks.slice(0, maxRows) || [];
  const cumAsks: number[] = [];
  let cum = 0;
  for (const ask of originalAsks) {
    cum += parseFloat(ask.size);
    cumAsks.push(cum);
  }
  const cumAsksReversed = cumAsks.reverse(); // Match displayAsks order
  const maxCumAsk =
    cumAsksReversed.length > 0 ? Math.max(...cumAsksReversed) : 1;

  const cumBids: number[] = [];
  cum = 0;
  for (const bid of displayBids) {
    cum += parseFloat(bid.size);
    cumBids.push(cum);
  }
  const maxCumBid = cumBids.length > 0 ? Math.max(...cumBids) : 1;

  const getMarket = (symbol: string) => {
    if (!symbol) return '';
    const parts = symbol.split('-');
    return parts.length > 1 ? `(${parts[1]})` : '';
  };

  const getSymbol = (symbol: string) => {
    if (!symbol) return '';
    const parts = symbol.split('-');
    return parts.length > 0 ? `(${parts[0]})` : '';
  };

  return (
    <div
      className={`  h-full  transition-all duration-300 bg-white dark:!bg-[#121212] text-[#121212] dark:!text-white
      ${isNotMobile ? `${collapsed ? 'w-[60px]' : 'w-[260px]'}` : `w-full`}`}
      ref={containerRef}
    >
      {/* Collapse/Expand Button */}
      {isNotMobile ? (
        <div className="flex justify-center px-4 pb-[18px] text-black text-sm font-normal">
          <div
            className={`w-full bg-brand rounded-lg py-3 relative flex items-center justify-center ${collapsed
              ? 'flex-col h-[130px] w-[150px]'
              : 'flex-row h-auto w-full'
              }`}
          >
            {collapsed ? (
              <>
                <div className="absolute right-[-3px] top-2">
                  <IconButton onClick={toggleCollapse}>
                    <ChevronRightIcon />
                  </IconButton>
                </div>
                <span className="text-sm mt-2 transform origin-center inline-block rotate-[-90deg] whitespace-nowrap">
                  Order Book
                </span>
              </>
            ) : (
              <>
                <span className="text-sm font-medium">Order Book</span>
                <div className="absolute right-1 top-3">
                  <IconButton onClick={toggleCollapse} size="small">
                    <ChevronLeftIcon />
                  </IconButton>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {!collapsed && (
        <div
          className={`tab-content card-bs table-temp3 pl-4 pr-3   ${isNotMobile ? 'h-[calc(100%-72px)]' : 'h-[500px]'
            }`}
        >
          {/* Order Book */}
          {/* Order Book Table Replacement */}
          <div className="bg-[#f1f1f1] dark:bg-cardColor rounded-lg w-full p-2">
            {/* Header */}
            <div className="flex dark:text-gray-300 text-xs font-medium py-2 px-3 border-b-2 border-black">
              <div className="w-1/3 min-w-[65px] max-w-[80px] text-left">
                Price {getMarket(currentSymbol)}
              </div>
              <div className="w-1/3 min-w-[70px] max-w-[100px] text-right">
                Quantity {getSymbol(currentSymbol)}
              </div>
              <div className="w-1/3 min-w-[65px] max-w-[100px] text-right">
                Total {getSymbol(currentSymbol)}
              </div>
            </div>
            {/* Body */}
            <div>
              {/* Asks (Sell Orders) - Red */}
              {displayAsks.map((ask, index) => (
                <div
                  key={`ask-${index}`}
                  className="relative flex py-2 px-3 dark:hover:bg-gray-700 hover:bg-gray-300 transition-colors"
                >
                  <div
                    className="absolute right-0 top-0 h-full bg-bgRed"
                    style={{
                      width: `${(cumAsksReversed[index] / maxCumAsk) * 100}%`,
                    }}
                  />
                  <div className="relative flex w-full">
                    <div className="text-down w-1/3 min-w-[60px] max-w-[80px] truncate text-xs text-left">
                      {formatPrice(ask.price)}
                    </div>
                    <div className="w-1/3 min-w-[70px] max-w-[80px] truncate text-xs  text-right">
                      {formatSize(ask.size)}
                    </div>
                    <div className="w-1/3 min-w-[65px] max-w-[80px] truncate text-xs  text-right">
                      {formatSize(cumAsksReversed[index].toString())}
                    </div>
                  </div>
                </div>
              ))}
              {/* Current Price Row */}
              {!(!orderBookData && connectionStatus !== 'error') && (
                <div className="flex py-2 px-3 gap-3  text-sm">
                  <div className=" font-bold flex items-center gap-1  truncate text-xs text-right">
                    {formatPrice(lastPrice)}
                  </div>
                  <div className="dark:text-brand  w-2/3 truncate text-xs font-bold text-left">
                    ≈ <span className="ml-2">{formatPrice(lastPrice)} USD</span>
                  </div>
                </div>
              )}
              {/* Bids (Buy Orders) - Green */}
              {displayBids.map((bid, index) => (
                <div
                  key={`bid-${index}`}
                  className="relative flex py-2 px-3 dark:hover:bg-gray-700 hover:bg-gray-300 transition-colors"
                >
                  <div
                    className="absolute left-0 top-0 h-full bg-bgGreen"
                    style={{ width: `${(cumBids[index] / maxCumBid) * 100}%` }}
                  />
                  <div className="relative flex w-full">
                    <div className="text-up w-1/3 min-w-[60px] max-w-[80px] truncate text-xs text-left">
                      {formatPrice(bid.price)}
                    </div>
                    <div className="w-1/3 min-w-[70px] max-w-[80px] truncate text-xs  text-right">
                      {formatSize(bid.size)}
                    </div>
                    <div className="w-1/3 min-w-[65px] max-w-[80px] truncate text-xs  text-right">
                      {formatSize(cumBids[index].toString())}
                    </div>
                  </div>
                </div>
              ))}

              {/* Loading state */}
              {!orderBookData && connectionStatus !== 'error' && (
                <>
                  {/* Skeleton rows for asks */}
                  {Array.from({ length: maxRows }).map((_, index) => (
                    <div
                      key={`skeleton-ask-${index}`}
                      className="flex py-2 px-3"
                    >
                      <div className="w-1/3 min-w-[60px] max-w-[80px] animate-pulse">
                        <div className="h-4 bg-gray-600 rounded"></div>
                      </div>
                      <div className="w-1/3 min-w-[70px] max-w-[80px] animate-pulse">
                        <div className="h-4 bg-gray-600 rounded"></div>
                      </div>
                      <div className="w-1/3 min-w-[65px]] max-w-[80px] animate-pulse">
                        <div className="h-4 bg-gray-600 rounded"></div>
                      </div>
                    </div>
                  ))}

                  {/* Skeleton rows for bids */}
                  {Array.from({ length: maxRows }).map((_, index) => (
                    <div
                      key={`skeleton-bid-${index}`}
                      className="flex py-2 px-3"
                    >
                      <div className="w-1/3 min-w-[60px] max-w-[80px] animate-pulse">
                        <div className="h-4 bg-gray-600 rounded"></div>
                      </div>
                      <div className="w-1/3 min-w-[70px] max-w-[80px] animate-pulse">
                        <div className="h-4 bg-gray-600 rounded"></div>
                      </div>
                      <div className="w-1/3 min-w-[65px]] max-w-[80px] animate-pulse">
                        <div className="h-4 bg-gray-600 rounded"></div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Error state */}
              {connectionStatus === 'error' && (
                <div className="py-4 px-3 text-center">
                  <div className="flex items-center justify-center gap-2 text-red-400 text-xs">
                    <span className="text-lg">⚠️</span>
                    <span>Failed to connect to order book</span>
                  </div>
                </div>
              )}

              {/* No data state */}
              {orderBookData &&
                displayAsks.length === 0 &&
                displayBids.length === 0 && (
                  <div className="py-4 px-3 text-center text-gray-400 text-xs">
                    No order book data available
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderBook;
